import { Button, Tag, Typography } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import { ComponentProps, FC, ReactNode, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import UpsellModal from "./UpsellModal";

type ActionButtonProps = ComponentProps<typeof Button> & {
  withUpsellModal?: boolean;
};

export function ActionButton({
  children,
  withUpsellModal = true,
  ...props
}: ActionButtonProps) {
  if (!withUpsellModal)
    return (
      <Button
        as={Link}
        shape="soft"
        intent="secondary"
        className="mt-sm"
        {...props}
      >
        {children}
      </Button>
    );

  return (
    <UpsellModal>
      <Button
        as={Link}
        shape="soft"
        intent="secondary"
        className="mt-sm"
        {...props}
      >
        {children}
      </Button>
    </UpsellModal>
  );
}

export type BaseCardProps = {
  title: string;
  text: string;
  orientation?: "horizontal" | "vertical";
  Icon: FC<SVGProps<SVGSVGElement>>;
  children?: ReactNode;
  tag?: "teasing" | "new";
  headerContent?: ReactNode;
};

function BaseCard({
  orientation = "horizontal",
  Icon,
  title,
  text,
  children,
  tag,
  headerContent,
}: BaseCardProps) {
  const { t } = useTranslation(["common"]);

  return (
    <div
      className={cn(
        "p-lg flex rounded-lg bg-white",
        orientation === "horizontal" ? "gap-x-lg" : "gap-y-sm flex-col"
      )}
    >
      <div className="shrink-0">
        <Icon
          role="img"
          className={orientation === "vertical" ? "mx-auto" : ""}
        />
      </div>
      <div className="w-full">
        <div className="mb-2xs gap-x-2xs flex items-center justify-between">
          <Typography as="h3" step="1" weight="bold">
            {title}
          </Typography>
          {tag === "new" && <Tag intent="positive" text={t("common:new")} />}
          {tag === "teasing" && (
            <Tag intent="primary" text={t("common:availableSoon")} />
          )}
          {headerContent}
        </div>
        <Typography>{text}</Typography>
        {children}
      </div>
    </div>
  );
}

export default BaseCard;
